import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";

interface Project {
  id: string;
  title_en: string;
  title_ps: string;
  title_da: string;
  description_en: string;
  description_ps: string;
  description_da: string;
  category: string;
  status: string;
  image_url: string;
  location: string;
  beneficiaries: number;
  progress: number;
  created_at: string;
}

const categoryColors: Record<string, string> = {
  Education: "bg-amber-100 text-amber-700",
  Infrastructure: "bg-sky-100 text-sky-700",
  Healthcare: "bg-rose-100 text-rose-700",
  "Emergency Relief": "bg-orange-100 text-orange-700",
  "Women Empowerment": "bg-violet-100 text-violet-700",
};

function getLocalizedField(project: Project, field: string, lang: string): string {
  const suffix = lang === "ps" ? "_ps" : lang === "da" ? "_da" : "_en";
  return (project as any)[field + suffix] || (project as any)[field + "_en"] || "";
}

export default function ProjectsPage() {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeStatus, setActiveStatus] = useState("all");
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(() => {
    setLoading(true);
    setError(null);
    supabase
      .from("projects")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data, error: err }) => {
        if (err) {
          console.error("Projects list fetch error:", err.message);
          setError(err.message);
        } else {
          setProjects((data as Project[]) ?? []);
        }
      })
      .catch((err: Error) => {
        console.error("Projects list unexpected error:", err.message);
        setError(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const categories = [
    { key: "All", label: t("projectsPage.filters.all") },
    { key: "Education", label: t("projectsPage.filters.education") },
    { key: "Healthcare", label: t("projectsPage.filters.healthcare") },
    { key: "Infrastructure", label: t("projectsPage.filters.infrastructure") },
    { key: "Emergency Relief", label: t("projectsPage.filters.humanitarian") },
    { key: "Women Empowerment", label: t("projectsPage.filters.community") },
  ];

  const filtered = projects.filter((p) => {
    const catMatch = activeCategory === "All" || p.category === activeCategory;
    const statusMatch = activeStatus === "all" || p.status === activeStatus;
    return catMatch && statusMatch;
  });

  return (
    <main className="pt-24">
      {/* Header */}
      <section className="py-14 bg-[#1B2838]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#3B9EF5] mb-3 block">{t("projectsPage.tag")}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("projectsPage.title")}
          </h1>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            {t("projectsPage.subtitle")}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-gray-100 sticky top-[100px] z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  activeCategory === cat.key
                    ? "bg-[#1B2838] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-1 bg-gray-100 rounded-full p-1">
            {[
              { value: "all", label: t("projectsPage.status.all") },
              { value: "active", label: t("projectsPage.status.active") },
              { value: "completed", label: t("projectsPage.status.completed") },
            ].map((s) => (
              <button
                key={s.value}
                onClick={() => setActiveStatus(s.value)}
                className={`px-4 py-1 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  activeStatus === s.value ? "bg-white text-[#1B2838]" : "text-gray-500"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-14 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-8 h-8 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50">
                <i className="ri-error-warning-line text-2xl text-red-400"></i>
              </div>
              <p className="text-gray-600 font-medium mb-1">Failed to load projects</p>
              <p className="text-gray-400 text-sm mb-5">{error}</p>
              <button
                onClick={fetchProjects}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B9EF5] text-[#1B2838] rounded-full font-semibold text-sm cursor-pointer hover:bg-[#2A8DE0] transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i> Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <i className="ri-folder-open-line text-5xl mb-4 block"></i>
              <p>{t("projectsPage.empty")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((project) => (
                <div
                  key={project.id}
                  className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-[#3B9EF5]/40 transition-all duration-300 group flex flex-col"
                >
                  <div className="relative h-48 sm:h-52 overflow-hidden flex-shrink-0">
                    <img
                      src={project.image_url}
                      alt={getLocalizedField(project, "title", i18n.language)}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[project.category] || "bg-gray-100 text-gray-600"}`}>
                        {project.category}
                      </span>
                    </div>
                    {project.status === "completed" && (
                      <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                        {t("projectsPage.badge.completed")}
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-bold text-[#1B2838] text-lg mb-2 leading-tight">
                      {getLocalizedField(project, "title", i18n.language)}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1 line-clamp-2">
                      {getLocalizedField(project, "description", i18n.language)}
                    </p>

                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                        <span>{t("projectsPage.progress")}: <strong className="text-[#1B2838]">{project.progress}%</strong></span>
                        <span className="font-semibold text-[#3B9EF5]">{project.beneficiaries.toLocaleString()} {t("projectsPage.beneficiaries")}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#3B9EF5] rounded-full"
                          style={{ width: `${Math.min(project.progress, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-map-pin-line"></i>
                          {project.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-group-line"></i>
                          {project.beneficiaries.toLocaleString()}
                        </span>
                      </div>
                      <Link
                        to={`/projects/${project.id}`}
                        className="text-[#3B9EF5] text-sm font-semibold hover:text-[#2A8DE0] cursor-pointer flex items-center gap-1 whitespace-nowrap"
                      >
                        {t("projectsPage.details")} <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}